// Proximity target: how close a lag has to finish, as a share of the putt,
// to read green (good) or yellow (mid). One continuous rule, so a 30 ft putt
// and a 50 ft putt are judged in proportion (Cole, 2026-09-29: "shouldn't
// they be totally dynamic"). It colors the leave readouts and progress boxes
// and sizes the green and yellow cup rings, on the phone and the TV alike.
// Display only: no score, leaderboard, or LPI reads it.

export type ProximityPreset = 'tight' | 'standard' | 'relaxed';

export const PROXIMITY_PRESETS: ReadonlyArray<{
  id: ProximityPreset;
  /** Green edge as a share of the putt distance; yellow is twice it. */
  goodShare: number;
}> = [
  { id: 'tight', goodShare: 0.05 },
  { id: 'standard', goodShare: 0.08 },
  { id: 'relaxed', goodShare: 0.1 },
];

export const DEFAULT_PROXIMITY_PRESET: ProximityPreset = 'standard';

/** Short putts never shrink the rings below these. */
export const MIN_GOOD_FT = 1;
export const MIN_MID_FT = 2;

/** Used when the putt distance is unknown (the old middle tier). */
const FALLBACK_TARGET_FT = 25;

export interface ProximityBands {
  /** Upper bound (inclusive) for the green band, in feet. */
  goodMaxFt: number;
  /** Upper bound (inclusive) for the yellow band, in feet. */
  midMaxFt: number;
}

export function normalizeProximityPreset(value: unknown): ProximityPreset {
  return PROXIMITY_PRESETS.some((preset) => preset.id === value) ? (value as ProximityPreset) : DEFAULT_PROXIMITY_PRESET;
}

export function proximityGoodShare(preset: ProximityPreset): number {
  return (PROXIMITY_PRESETS.find((p) => p.id === preset) ?? PROXIMITY_PRESETS[1]).goodShare;
}

/** "5% / 10%": the preset as the green and yellow shares. */
export function proximityPresetLabel(preset: ProximityPreset): string {
  const share = Math.round(proximityGoodShare(preset) * 100);
  return `${share}% / ${share * 2}%`;
}

export function proximityBandsFor(
  targetFt: number | null | undefined,
  preset: ProximityPreset = DEFAULT_PROXIMITY_PRESET,
): ProximityBands {
  const distance = targetFt != null && Number.isFinite(targetFt) && targetFt > 0 ? targetFt : FALLBACK_TARGET_FT;
  const share = proximityGoodShare(normalizeProximityPreset(preset));
  const round = (ft: number) => Math.round(ft * 100) / 100;
  return {
    goodMaxFt: round(Math.max(MIN_GOOD_FT, distance * share)),
    midMaxFt: round(Math.max(MIN_MID_FT, distance * share * 2)),
  };
}
